import { useState, useCallback } from 'react';
import {
  CodeSnippet,
  Content,
  Header,
  Link,
  Page,
} from '@backstage/core-components';
import {
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import InfoIcon from '@material-ui/icons/Info';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityKindPicker,
  EntityTagPicker,
} from '@backstage/plugin-catalog-react';

interface GitHubProfile {
  displayName?: string;
  username?: string;
}

export const ProjectRocketPage = () => {
  const githubAuthApi = useApi(githubAuthApiRef);
  const [ghProfile, setGhProfile] = useState<GitHubProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(false);

  const handleLogin = useCallback(async () => {
    try {
      setAuthLoading(true);
      const profile = await githubAuthApi.getProfile();
      if (profile) {
        setGhProfile(profile);
      }
    } catch (error) {
      // Removed console statement to fix ESLint error
    } finally {
      setAuthLoading(false);
    }
  }, [githubAuthApi]);

  const catalogColumnsFunc: CatalogTableColumnsFunc = _entityListContext => {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createTagsColumn(),
    ];
  };

  return (
    <Page themeId="home">
      <Header
        title="Welcome to App-as-a-Service launchpad"
        subtitle="Your AI-powered project launcher"
      />
      <Content>
        {/* Introduction Section */}
        <Box mb={4}>
          <Typography variant="h5" gutterBottom>
            What is App-as-a-service?
          </Typography>
          <Typography variant="body1" paragraph>
            App-as-a-Service is your gateway to launching new projects
            effortlessly. With AI-powered assistance and best-practice
            templates, you can create fully functional repositories in
            minutes—no deep engineering knowledge required.
          </Typography>
          <Typography variant="body1" paragraph>
            Our platform is self-serve, empowering you to focus on innovation
            while we handle the setup. Let AI be your co-pilot in shaping your
            project.
          </Typography>
        </Box>

        {/* Prerequisites Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Prerequisites
          </Typography>
          <Typography variant="body2" paragraph>
            Before you begin, ensure you have the following:
          </Typography>
          <Box component="ul" pl={3} mb={2}>
            <li>
              <Typography variant="body2">
                A working GitHub account linked to our enterprise.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                An approved AI tool, such as VS Code or Claude Desktop.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                For Claude Desktop users: Install the <strong>git-dxt</strong>{' '}
                extension.
              </Typography>
            </li>
          </Box>
        </Box>

        {/* Step-by-Step Guidance */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Get Started in 3 Easy Steps
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Step 1: Verify GitHub Access
                  </Typography>
                  <Typography variant="body2">
                    Connect your GitHub account to enable seamless repository
                    operations.
                    {ghProfile ? (
                      <Typography variant="body2" paragraph>
                        Logged in as:{' '}
                        <strong>
                          {ghProfile.displayName || ghProfile.username}
                        </strong>
                      </Typography>
                    ) : (
                      <Typography variant="body2" paragraph>
                        Please log in to GitHub to proceed.
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleLogin}
                      disabled={authLoading || !!ghProfile}
                    >
                      {ghProfile ? 'Logged in' : 'Log in to GitHub'}
                    </Button>
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary" startIcon={<InfoIcon />}>
                    <Link
                      component={RouterLink}
                      to="/docs/default/component/app-as-a-service/01-howto-pre/#required-prerequisites"
                    >
                      Set up your GitHub Account
                    </Link>
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Step 2: Test your AI Tool
                  </Typography>
                  <Typography variant="body2">
                    Copy the following prompt into your AI tool to ensure it's
                    set up correctly:
                  </Typography>
                  <CodeSnippet
                    language="markdown"
                    showCopyCodeButton
                    text={`
Let's verify your setup for building Access Nexus apps!

I will attempt to clone this demo repository to test that your tools are configured correctly:
https://github.com/Access-Nexus-Apps/demo-repository

REQUIRED tools (setup fails if missing):
- Filesystem access to working directories
- Git operations (clone, status, commit, etc.)

OPTIONAL tools (nice to have):
- npm tools (for installing packages and running scripts - if unavailable, your experience will be slightly limited)

What I'll do:
1. Check filesystem access
2. Clone the demo repository to verify git is working
3. If the repository already exists, clone it to a fresh folder with a different name
4. Run git status to verify
5. Check if npm tools are available (this won't affect pass/fail)

---

✅ If filesystem AND git both work: Your setup is verified! 
👉 **Next step:** Go to the Access Nexus Platform and complete Step 3:https://portal.ax.accessacloud.com/launchpad

❌ If filesystem OR git fails: Setup is incomplete. 
👉 **You need to configure your environment.** Visit the Access Nexus setup documentation here: https://portal.ax.accessacloud.com/docs/default/component/app-as-a-service/02-howto-vibe/

⚠️ Note on npm: If npm tools aren't detected, I'll let you know, but this won't prevent you from proceeding. You can still build apps, just with some limitations.

---

IMPORTANT RULES FOR THIS CHAT:
- This chat is ONLY for setup verification
- Do NOT provide technical configuration instructions or code examples for MCP servers
- Do NOT tell users how to edit config files or install specific packages
- If tools are missing or users ask for help, ONLY direct them to: https://portal.ax.accessacloud.com/docs/default/component/app-as-a-service/02-howto-vibe/
- Do NOT make up URLs or suggest alternative documentation sites
- For ANY questions about next steps, what to do, how to fix issues, or how to proceed: Direct users to the setup documentation link above
- Do NOT continue building apps in this chat
- After completing Step 3 on the platform, start a NEW chat to begin building your app
- I cannot help you build apps until you've initialized a project through the Access Nexus Platform
`}
                  />
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary" startIcon={<InfoIcon />}>
                    <Link
                      component={RouterLink}
                      to="/docs/default/component/app-as-a-service/02-howto-vibe/"
                    >
                      Configure your AI (Claude or VS Code)
                    </Link>
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Step 3: Launch Your Project
                  </Typography>
                  <Typography variant="body2">
                    Follow the guided steps to launch your project effortlessly.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="secondary"
                    component={RouterLink}
                    to="/create"
                  >
                    View Templates
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<FlightTakeoffIcon />}
                    component={RouterLink}
                    to="/create/templates/default/spa-vite"
                  >
                    Launch Now
                  </Button>
                </CardActions>
                <CardActions>
                  <Button size="small" color="primary" startIcon={<InfoIcon />}>
                    <Link
                      component={RouterLink}
                      to="/docs/default/component/app-as-a-service/03-howto-publish/"
                    >
                      What's Next?
                    </Link>
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Apps-as-a-Service Components Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Explore what other people are building
          </Typography>
          <Typography variant="body2" paragraph>
            Get inspired and see what others have built.
          </Typography>
          <EntityListProvider>
            <EntityKindPicker
              allowedKinds={['Component']}
              initialFilter="component"
              hidden
            />
            <EntityTagPicker initialFilter={['apps-as-a-service']} hidden />
            <CatalogTable
              columns={catalogColumnsFunc}
              tableOptions={{
                sorting: true,
                paging: true,
                pageSize: 10,
                pageSizeOptions: [5, 10, 25],
              }}
            />
          </EntityListProvider>
        </Box>
      </Content>
    </Page>
  );
};

export default ProjectRocketPage;
